import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePartnerAccount } from '../../providers/PartnerAccount.provider';
import { usePartnerAccountBusinessCase } from '../../providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '../../providers/HubSpotData.provider';
import { useConfirmationDialogue } from '../../providers/ConfirmationDialogue.provider';
import { usePendingData } from '../../providers/PendingData.provider';
import { usePartnerLogin } from '../../providers/PartnerLogin.provider';
import { useCompanyProfileFields } from '../../providers/CompanyProfileFields.provider';
import { useDocument } from '../../providers/Document.provider';
import { usePartnerAccountMatrix } from '../../providers/PartnerAccountsMatrix.provider';
import useFetchHubSpotDealDetails from '../useApi/useFetchHubSpotDealDetails';

const usePostPartnerAccountBusinessCaseDialogue = (): {
  destination?: string;
  navigate: () => void;
  showDialogue: () => void;
} => {
  const history = useHistory();
  const [destination, setDestination] = useState<string>();

  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UsePostPartnerAccountBusinessCaseDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { provideProps, showDialogue } = useConfirmationDialogue();
  const { reset: resetPendingData } = usePendingData();
  const { reset: resetPartnerAccount } = usePartnerAccount();
  const { response: partnerLoginResponse } = usePartnerLogin();
  const { isInsideSales } = partnerLoginResponse;
  const { response: businessCaseResponse, reset: resetBusinessCase } = usePartnerAccountBusinessCase();
  const { response: hubSpotDataResponse, reset: resetHubSpotData } = useHubSpotData();
  const { response: accountMatrixResponse, reset: resetAccountMatrix } = usePartnerAccountMatrix();
  const { response: documentUploadResponse, reset: resetDocumentUpload } = useDocument();
  const [, , , resetHubSpotDealData] = useFetchHubSpotDealDetails({});
  const { setMakeFieldsVisible, setFromReviewPage } = useCompanyProfileFields();

  const success =
    businessCaseResponse?.success &&
    (isInsideSales
      ? (hubSpotDataResponse?.success ?? true) &&
        (accountMatrixResponse?.success ?? true) &&
        documentUploadResponse?.success
      : true);
  const errorMessage =
    businessCaseResponse?.errorMessage ||
    (isInsideSales
      ? hubSpotDataResponse?.errorMessage || accountMatrixResponse?.errorMessage || documentUploadResponse?.errorMessage
      : '') ||
    '';

  const navigate = () => {
    if (destination) history.push(destination);
  };

  const handleReset = () => {
    resetPendingData();
    resetPartnerAccount();
    resetBusinessCase();
    resetHubSpotData();
    resetAccountMatrix();
    resetDocumentUpload();
    setMakeFieldsVisible(false);
    setFromReviewPage(false);
    resetHubSpotDealData();

    setDestination('/accounts');
  };

  useEffect(() => {
    const handlers = {
      default: () =>
        provideProps({
          title: tGlobal('processing'),
          subTitle: '',
          status: 'alert',
          confirm: {
            label: tGlobal('pleaseWait'),
            action: () => {},
          },
        }),
      success: () =>
        provideProps({
          title: t('successTitle', { defaultValue: 'Success! Your account has been created.' }),
          subTitle: '',
          status: 'success',
          confirm: {
            label: tGlobal('Buttons.backToAccounts'),
            action: () => handleReset(),
          },
        }),
      errorMessage: () => {
        provideProps({
          title: t('errorTitle', {
            defaultValue:
              'Error! Your account has been created. However, there was an error submitting the business case.',
          }),
          status: 'error',
          subTitle: errorMessage,
          confirm: {
            label: tGlobal('Buttons.backToAccounts'),
            action: () => handleReset(),
          },
        });
      },
    };

    if (success) return handlers.success();

    if (typeof errorMessage !== 'undefined' && errorMessage !== '') return handlers.errorMessage();

    return handlers.default();
  }, [success, errorMessage]);

  return { destination, navigate, showDialogue };
};

export default usePostPartnerAccountBusinessCaseDialogue;
